// Pharmacy → 📈 Stock insights (Phase 1 quotation G16; API contract §6aa).
// Three read-only views over confirmed movements: what to reorder, how each
// item moves (FSN / ABC), and unusual stock movements this week. Nothing here
// changes stock or places an order.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { branchKey } from '@/lib/queryKeys';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { usePharmacyStores } from './usePharmacyStores';

type View = 'reorder' | 'classes' | 'alerts';

interface ReorderItem {
  medicine_id: number; generic_name: string; brand_name: string | null; strength: string | null;
  on_hand: number; in_transit: number; owed_on_indents: number; on_order: number;
  avg_daily_30: number; avg_daily_90: number; days_of_cover: number | null; target: number; suggested_qty: number;
  min_level: number | null; max_level: number | null;
}
interface ReorderResponse { store: { id: number; name: string; can_receive: boolean }; settings: { lead: number; review: number; safety: number }; items: ReorderItem[] }
interface ClassItem { medicine_id: number; generic_name: string; brand_name: string | null; strength: string | null; weeks_moved: number; units: number; value: number; on_hand: number; stock_value: number; fsn: 'F' | 'S' | 'N'; abc: 'A' | 'B' | 'C' }
interface Alert { kind: string; key: string; at: string; severity: 'warning' | 'info'; message: string }

const name = (r: { generic_name: string; brand_name: string | null; strength: string | null }) =>
  `${r.generic_name}${r.strength ? ` ${r.strength}` : ''}${r.brand_name ? ` (${r.brand_name})` : ''}`;
const money = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const FSN_LABEL = { F: 'Fast', S: 'Slow', N: 'Non-moving' } as const;
const ALERT_LABEL: Record<string, string> = {
  large_adjustment: 'Large manual change', repeated_corrections: 'Repeated corrections',
  loss_after_receipt: 'Written off soon after receipt', sales_spike: 'Sales spike',
};

export default function StockInsightsTab() {
  const [view, setView] = useState<View>('reorder');
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[13px] text-ink-muted">Worked out from sales, transfers and adjustments already recorded. Suggestions only — nothing is ordered or changed.</div>
        <SegmentedControl<View> size="sm" value={view} onChange={setView}
          options={[{ value: 'reorder', label: 'Reorder' }, { value: 'classes', label: 'Movement classes' }, { value: 'alerts', label: 'Unusual movements' }]} />
      </div>
      {view === 'reorder' && <ReorderView />}
      {view === 'classes' && <ClassesView />}
      {view === 'alerts' && <AlertsView />}
    </div>
  );
}

function ReorderView() {
  const stores = usePharmacyStores();
  const [storeId, setStoreId] = useState<number | null>(null);
  const [onlyToOrder, setOnlyToOrder] = useState(true);
  const effective = storeId ?? stores.data?.channels.receiving ?? stores.data?.stores[0]?.id ?? null;
  const q = useQuery({
    queryKey: branchKey('pharmacy-insights', 'reorder', effective),
    queryFn: () => api.get<ReorderResponse>('/pharmacy-insights/reorder', { query: { store_id: effective as number } }),
    enabled: effective != null,
  });
  const rows = (q.data?.items ?? []).filter((r) => !onlyToOrder || r.suggested_qty > 0);
  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-[12.5px]">
        <label className="flex items-center gap-2 text-ink-muted">Store
          <select className="h-8 w-auto min-w-[160px]" value={effective ?? ''} onChange={(e) => setStoreId(Number(e.target.value))}>
            {(stores.data?.stores ?? []).filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={onlyToOrder} onChange={(e) => setOnlyToOrder(e.target.checked)} /> Only items to order</label>
        {q.data && (
          <span className="ml-auto text-ink-muted">
            Order enough for {q.data.settings.lead} days' delivery + {q.data.settings.review} days until the next order + {q.data.settings.safety} days' safety stock (Admin → Pharmacy Policies).
          </span>
        )}
      </div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState message="Could not load suggestions" onRetry={() => q.refetch()} />
        : rows.length === 0 ? <EmptyState label={onlyToOrder ? 'Nothing needs ordering for this store.' : 'No stock or use recorded in this store.'} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Medicine</TableHead><TableHead className="text-right">Use / day</TableHead><TableHead className="text-right">On hand</TableHead>
              <TableHead className="text-right">Days left</TableHead><TableHead className="text-right">Coming</TableHead><TableHead className="text-right">Suggested order</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.medicine_id}>
                  <TableCell>{name(r)}</TableCell>
                  <TableCell className="text-right" title={`Last 30 days ${r.avg_daily_30} · last 90 days ${r.avg_daily_90}`}>{Math.max(r.avg_daily_30, r.avg_daily_90)}</TableCell>
                  <TableCell className="text-right">{r.on_hand}</TableCell>
                  <TableCell className="text-right">{r.days_of_cover == null ? '—' : r.days_of_cover}</TableCell>
                  <TableCell className="text-right" title="In transit + owed on indents + on purchase order">{r.in_transit + r.owed_on_indents + r.on_order || '—'}</TableCell>
                  <TableCell className="text-right font-semibold">{r.suggested_qty || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      {q.data && !q.data.store.can_receive && <div className="mt-2 text-[11.5px] text-ink-muted">This store is restocked by transfer from the main store — raise an indent for these quantities.</div>}
    </Card>
  );
}

function ClassesView() {
  const [search, setSearch] = useState('');
  const q = useQuery({ queryKey: branchKey('pharmacy-insights', 'classes'), queryFn: () => api.get<ClassItem[]>('/pharmacy-insights/classes') });
  const counts = useMemo(() => {
    const c = { F: 0, S: 0, N: 0, A: 0, B: 0, C: 0 };
    for (const r of q.data ?? []) { c[r.fsn] += 1; c[r.abc] += 1; }
    return c;
  }, [q.data]);
  const rows = (q.data ?? []).filter((r) => !search || name(r).toLowerCase().includes(search.toLowerCase()));
  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-[12.5px]">
        <Input className="h-8 max-w-[240px] text-[12.5px]" placeholder="Find a medicine" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="text-ink-muted">Last 13 weeks · Fast {counts.F} · Slow {counts.S} · Non-moving {counts.N} · A {counts.A} · B {counts.B} · C {counts.C}</span>
      </div>
      <div className="mb-2 text-[11.5px] text-ink-muted">
        Fast: sold in 8+ of the last 13 weeks; Slow: 2–7 weeks; Non-moving: fewer. A: the items making up the first 70% of what was used, by cost; B: the next 20%; C: the rest.
      </div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState message="Could not load classes" onRetry={() => q.refetch()} /> : rows.length === 0 ? <EmptyState label="No stock or sales at this branch." /> : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Medicine</TableHead><TableHead>Moves</TableHead><TableHead>Value</TableHead>
            <TableHead className="text-right">Weeks sold</TableHead><TableHead className="text-right">Used (13 wk)</TableHead><TableHead className="text-right">On hand</TableHead><TableHead className="text-right">Stock value</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.medicine_id}>
                <TableCell>{name(r)}</TableCell>
                <TableCell><Badge variant={r.fsn === 'F' ? 'success' : r.fsn === 'S' ? 'info' : 'warn'}>{FSN_LABEL[r.fsn]}</Badge></TableCell>
                <TableCell><Badge variant={r.abc === 'A' ? 'danger' : r.abc === 'B' ? 'caution' : 'neutral'}>{r.abc}</Badge></TableCell>
                <TableCell className="text-right">{r.weeks_moved}</TableCell>
                <TableCell className="text-right">{Math.round(r.units)}</TableCell>
                <TableCell className="text-right">{r.on_hand ?? 0}</TableCell>
                <TableCell className="text-right">{money(r.stock_value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function AlertsView() {
  const q = useQuery({
    queryKey: branchKey('pharmacy-insights', 'alerts'),
    queryFn: () => api.get<{ alerts: Alert[] }>('/pharmacy-insights/alerts'),
  });
  return (
    <Card>
      <div className="mb-2 text-[11.5px] text-ink-muted">
        Last 7 days: a manual change of a quarter or more of a batch (10+ units), three or more corrections by one person, a write-off within 7 days of
        receiving the batch, and a medicine selling more than three times its usual weekly variation.
      </div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState message="Could not load alerts" onRetry={() => q.refetch()} />
        : (q.data?.alerts ?? []).length === 0 ? <EmptyState label="Nothing unusual this week." /> : (
          <ul className="flex list-none flex-col divide-y divide-line p-0 text-[12.5px]">
            {q.data!.alerts.map((a) => (
              <li key={a.key} className="flex flex-wrap items-center gap-2 py-2">
                <Badge variant={a.severity === 'warning' ? 'warn' : 'info'}>{ALERT_LABEL[a.kind] ?? a.kind}</Badge>
                <span className="min-w-0 flex-1 text-ink">{a.message}</span>
                <span className="text-ink-muted">{new Date(a.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </li>
            ))}
          </ul>
        )}
    </Card>
  );
}
